import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

export const THEME_STORAGE_KEY = 'myexam-theme-mode';

export const ThemeContext = createContext(null);

function getSystemPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function readStoredThemeMode() {
  if (typeof window === 'undefined') return THEME_MODES.SYSTEM;
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === THEME_MODES.LIGHT || stored === THEME_MODES.DARK || stored === THEME_MODES.SYSTEM) {
    return stored;
  }
  return THEME_MODES.SYSTEM;
}

export function resolveTheme(mode) {
  if (mode === THEME_MODES.DARK) return THEME_MODES.DARK;
  if (mode === THEME_MODES.LIGHT) return THEME_MODES.LIGHT;
  return getSystemPrefersDark() ? THEME_MODES.DARK : THEME_MODES.LIGHT;
}

export function applyResolvedTheme(resolved) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === THEME_MODES.DARK);
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved === THEME_MODES.DARK ? 'dark' : 'light';
}

export function ThemeProvider({ children }) {
  const [themeMode, setThemeModeState] = useState(readStoredThemeMode);
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(readStoredThemeMode()));

  const setThemeMode = useCallback((mode) => {
    if (mode !== THEME_MODES.LIGHT && mode !== THEME_MODES.DARK && mode !== THEME_MODES.SYSTEM) {
      return;
    }
    localStorage.setItem(THEME_STORAGE_KEY, mode);
    setThemeModeState(mode);
    const resolved = resolveTheme(mode);
    setResolvedTheme(resolved);
    applyResolvedTheme(resolved);
  }, []);

  useEffect(() => {
    const resolved = resolveTheme(themeMode);
    setResolvedTheme(resolved);
    applyResolvedTheme(resolved);
  }, [themeMode]);

  useEffect(() => {
    if (themeMode !== THEME_MODES.SYSTEM || typeof window === 'undefined') return undefined;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const resolved = resolveTheme(THEME_MODES.SYSTEM);
      setResolvedTheme(resolved);
      applyResolvedTheme(resolved);
    };

    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [themeMode]);

  const value = useMemo(
    () => ({
      themeMode,
      resolvedTheme,
      setThemeMode,
      isDark: resolvedTheme === THEME_MODES.DARK,
    }),
    [themeMode, resolvedTheme, setThemeMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
